import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';

import { useMiniAppApi } from '../../hooks';
import type { MiniAppActionState, MiniAppContext } from '../../types';

import { CLINICAL_FINDINGS_AGENT_ID } from './constants';
import { getFindingsVersion, subscribeFindings } from './findings-refresh';

export const FINDINGS_POLL_INTERVAL_MS = 15_000;
export const FINDINGS_POLL_CAP_MS = 120_000;

export function useFindingsActionState(
  context: MiniAppContext,
): MiniAppActionState {
  const { tenantId, appointmentId } = context;
  const api = useMiniAppApi(tenantId, CLINICAL_FINDINGS_AGENT_ID);
  const isCarePlansEnabled = useIsPostHogFeatureFlagEnabled('CARE_PLANS');
  const version = useSyncExternalStore(subscribeFindings, getFindingsVersion);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const seenForAppointmentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isCarePlansEnabled || !appointmentId) {
      setPendingCount(null);
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let capId: ReturnType<typeof setTimeout> | undefined;

    const stopPolling = () => {
      clearInterval(intervalId);
      clearTimeout(capId);
    };

    const fetchOnce = async () => {
      try {
        const res = await api.listFindings(appointmentId);
        if (cancelled) return;
        const findings = res.status === 200 ? res.body.findings : [];
        setPendingCount(
          findings.filter(
            (finding) => (finding.decision ?? 'pending') === 'pending',
          ).length,
        );
        if (findings.length > 0) {
          seenForAppointmentRef.current = appointmentId;
          stopPolling();
        }
      } catch {
        if (!cancelled) setPendingCount(0);
      }
    };

    setPendingCount(null);
    void fetchOnce();

    if (seenForAppointmentRef.current !== appointmentId) {
      intervalId = setInterval(
        () => void fetchOnce(),
        FINDINGS_POLL_INTERVAL_MS,
      );
      capId = setTimeout(stopPolling, FINDINGS_POLL_CAP_MS);
    }

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [api, appointmentId, isCarePlansEnabled, version]);

  const isLoaded = pendingCount !== null;
  const count = pendingCount ?? 0;
  return {
    review: { hidden: !isLoaded || count === 0, badge: count },
  };
}
